"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslation } from "react-i18next";
import { formatIssueDate } from "@/shared/lib/format/dates";
import { formatUzsValue } from "@/shared/lib/format/numbers";
import { cn } from "@/shared/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import type { ReferentPriceEntry } from "../api/referent-prices";
import { splitReferentPrices } from "../lib/split";
import { ReferentPriceCell, type ReferentPriceCellTone } from "./referent-price-cell";
type ReferentPriceField = "wholesalePrice" | "retailPrice";
type ReferentPriceDualCellProps = {
  entries: ReferentPriceEntry[] | null | undefined;
  field: ReferentPriceField;
  intlLocale: string;
  tone?: ReferentPriceCellTone;
  hideDecimals?: boolean;
  /** Stack vertically (cells in tables); default true. Set false for inline contexts. */
  stack?: boolean;
};
function pickValue(entry: ReferentPriceEntry | null, field: ReferentPriceField): number | null {
  if (!entry) return null;
  return field === "wholesalePrice" ? entry.wholesalePrice : entry.retailPrice;
}
function pickDate(entry: ReferentPriceEntry | null): string | null {
  if (!entry) return null;
  return entry.enforceAt ?? entry.crawledAt ?? entry.updatedAt ?? null;
}
export function ReferentPriceDualCell(t0) {
  const $ = _c(50);
  const {
    entries,
    field,
    intlLocale,
    tone: t1,
    hideDecimals: t2,
    stack: t3
  } = t0;
  const tone = t1 === undefined ? "default" : t1;
  const hideDecimals = t2 === undefined ? false : t2;
  const stack = t3 === undefined ? true : t3;
  const {
    t
  } = useTranslation();
  let T0;
  let T1;
  let t10;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let upcomingLabel;
  let upcomingTooltip;
  if ($[0] !== entries || $[1] !== field || $[2] !== hideDecimals || $[3] !== intlLocale || $[4] !== stack || $[5] !== t || $[6] !== tone) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const {
        current,
        upcoming
      } = splitReferentPrices(entries);
      const currentValue = pickValue(current, field);
      const upcomingValue = pickValue(upcoming, field);
      const upcomingDate = upcoming?.enforceAt ?? null;
      if (upcomingValue == null || !upcomingDate) {
        t10 = <ReferentPriceCell value={currentValue} capturedAt={pickDate(current)} intlLocale={intlLocale} tone={tone} hideDecimals={hideDecimals} />;
        break bb0;
      }
      const formattedDate = formatIssueDate(upcomingDate, intlLocale);
      upcomingLabel = t("wholesaleOffers.referentPriceUpcomingLabel", {
        date: formattedDate
      });
      upcomingTooltip = t("wholesaleOffers.referentPriceUpcomingTooltip", {
        price: formatUzsValue(upcomingValue, {
          noDecimals: hideDecimals
        }),
        date: formattedDate
      });
      if (currentValue == null) {
        const t11 = stack ? "flex flex-col items-end leading-tight" : "inline-flex items-baseline gap-2";
        let t12;
        if ($[18] !== t11) {
          t12 = cn("cursor-help", t11);
          $[18] = t11;
          $[19] = t12;
        } else {
          t12 = $[19];
        }
        const t13 = <span className={t12}><span className="text-muted-foreground italic tabular-nums">{formatUzsValue(upcomingValue, {
              noDecimals: hideDecimals
            })}</span><span className="text-muted-foreground/80 text-[10px] tracking-wide uppercase">{upcomingLabel}</span></span>;
        let t14;
        if ($[20] !== t13) {
          t14 = <TooltipTrigger asChild={true}>{t13}</TooltipTrigger>;
          $[20] = t13;
          $[21] = t14;
        } else {
          t14 = $[21];
        }
        t10 = <Tooltip>{t14}<TooltipContent>{upcomingTooltip}</TooltipContent></Tooltip>;
        break bb0;
      }
      const t11 = tone === "over" && "text-rose-700 font-medium dark:text-rose-300";
      const t12 = tone === "near" && "text-amber-700 font-medium dark:text-amber-300";
      const t13 = tone === "default" && "text-muted-foreground";
      let t14;
      if ($[22] !== t11 || $[23] !== t12 || $[24] !== t13) {
        t14 = cn("tabular-nums", t11, t12, t13);
        $[22] = t11;
        $[23] = t12;
        $[24] = t13;
        $[25] = t14;
      } else {
        t14 = $[25];
      }
      const currentTone = t14;
      T1 = Tooltip;
      T0 = TooltipTrigger;
      t9 = true;
      const t15 = stack ? "flex flex-col items-end leading-tight" : "inline-flex items-baseline gap-2";
      if ($[26] !== t15) {
        t7 = cn("cursor-help", t15);
        $[26] = t15;
        $[27] = t7;
      } else {
        t7 = $[27];
      }
      t8 = <span className={currentTone}>{formatUzsValue(currentValue, {
          noDecimals: hideDecimals
        })}</span>;
      t4 = "text-muted-foreground/80 text-xs tabular-nums";
      if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <span aria-hidden={true}>→ </span>;
        $[28] = t5;
      } else {
        t5 = $[28];
      }
      t6 = formatUzsValue(upcomingValue, {
        noDecimals: hideDecimals
      });
    }
    $[0] = entries;
    $[1] = field;
    $[2] = hideDecimals;
    $[3] = intlLocale;
    $[4] = stack;
    $[5] = t;
    $[6] = tone;
    $[7] = T0;
    $[8] = T1;
    $[9] = t10;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = upcomingLabel;
    $[17] = upcomingTooltip;
  } else {
    T0 = $[7];
    T1 = $[8];
    t10 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
    upcomingLabel = $[16];
    upcomingTooltip = $[17];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[29] !== upcomingLabel) {
    t11 = <span className="ml-1 text-[10px] tracking-wide uppercase">{upcomingLabel}</span>;
    $[29] = upcomingLabel;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t4 || $[33] !== t5 || $[34] !== t6) {
    t12 = <span className={t4}>{t5}{t6}{t11}</span>;
    $[31] = t11;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t12 || $[37] !== t7 || $[38] !== t8) {
    t13 = <span className={t7}>{t8}{t12}</span>;
    $[36] = t12;
    $[37] = t7;
    $[38] = t8;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== T0 || $[41] !== t13 || $[42] !== t9) {
    t14 = <T0 asChild={t9}>{t13}</T0>;
    $[40] = T0;
    $[41] = t13;
    $[42] = t9;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== upcomingTooltip) {
    t15 = <TooltipContent>{upcomingTooltip}</TooltipContent>;
    $[44] = upcomingTooltip;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== T1 || $[47] !== t14 || $[48] !== t15) {
    t16 = <T1>{t14}{t15}</T1>;
    $[46] = T1;
    $[47] = t14;
    $[48] = t15;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  return t16;
}